import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErrorMessage } from "../../lib/api";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { formatCLP, formatUSD } from "../../lib/format";

export default function AssignPurchase() {
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [products, setProducts] = useState([]);
    const [search, setSearch] = useState("");
    const [form, setForm] = useState({
        user_id: "",
        product_id: "",
        amount_paid: 0,
        currency: "CLP",
        payment_method: "manual",
        status: "paid",
        proof_file_url: "",
        internal_notes: "",
    });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        api.get("/admin/users", { params: search ? { q: search } : {} }).then(({ data }) => setUsers(data || []));
    }, [search]);
    useEffect(() => {
        api.get("/admin/products").then(({ data }) => setProducts(data || []));
    }, []);

    const selectedProduct = products.find((p) => p.id === form.product_id);
    useEffect(() => {
        if (selectedProduct) {
            const v = form.currency === "USD" ? selectedProduct.price_usd : selectedProduct.price_clp;
            setForm((f) => ({ ...f, amount_paid: v || 0 }));
        }
        // eslint-disable-next-line
    }, [form.product_id, form.currency]);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.user_id || !form.product_id) {
            toast.error("Select a user and a product");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/admin/purchases", {
                ...form,
                amount_paid: Number(form.amount_paid) || 0,
            });
            toast.success("Plugin assigned successfully.");
            navigate("/admin/purchases");
        } catch (err) {
            toast.error(apiErrorMessage(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <DashboardLayout role="admin">
            <div className="mb-8" data-testid="assign-purchase-page">
                <div className="label-mono mb-3">/ Assign purchase to user</div>
                <h1 className="font-heading font-black text-3xl lg:text-4xl tracking-tighter">Assign purchase</h1>
                <p className="text-avx-muted mt-2">Select a customer and a plugin to register a quick purchase.</p>
            </div>

            <form onSubmit={submit} className="grid lg:grid-cols-2 gap-6 max-w-5xl" data-testid="assign-form">
                <div className="panel p-6 space-y-4">
                    <div className="label-mono">/ Cliente</div>
                    <Input placeholder="Search by name or email…" value={search} onChange={(e) => setSearch(e.target.value)} className="bg-avx-bg border-avx-line h-11" data-testid="assign-search" />
                    <div className="max-h-72 overflow-y-auto border border-avx-line rounded-sm divide-y divide-avx-line">
                        {users.length === 0 ? (
                            <div className="p-4 text-sm text-avx-muted">No results.</div>
                        ) : (
                            users.map((u) => (
                                <button type="button" key={u.id} onClick={() => setForm({ ...form, user_id: u.id })}
                                    className={`w-full text-left px-3 py-2 hover:bg-avx-surface/60 transition-colors ${form.user_id === u.id ? "bg-avx-blue/10 border-l-2 border-avx-blue" : ""}`}
                                    data-testid={`assign-user-${u.id}`}>
                                    <div className="text-sm">{u.name} <span className="label-mono ml-2">{u.role}</span></div>
                                    <div className="font-mono text-xs text-avx-muted">{u.email}</div>
                                </button>
                            ))
                        )}
                    </div>
                </div>

                <div className="panel p-6 space-y-4">
                    <div className="label-mono">/ Purchase details</div>
                    <div>
                        <Label className="label-mono">Plugin</Label>
                        <Select value={form.product_id} onValueChange={(v) => setForm({ ...form, product_id: v })}>
                            <SelectTrigger className="mt-2 bg-avx-bg border-avx-line h-11" data-testid="assign-product"><SelectValue placeholder="Select plugin" /></SelectTrigger>
                            <SelectContent>
                                {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        {selectedProduct && (
                            <div className="font-mono text-xs text-avx-muted mt-2">
                                Suggested price: {formatCLP(selectedProduct.price_clp)} · {formatUSD(selectedProduct.price_usd)}
                            </div>
                        )}
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                        <div>
                            <Label className="label-mono">Currency</Label>
                            <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                                <SelectTrigger className="mt-2 bg-avx-bg border-avx-line h-11"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="CLP">CLP</SelectItem>
                                    <SelectItem value="USD">USD</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="col-span-2">
                            <Label className="label-mono">Amount paid</Label>
                            <Input type="number" step="0.01" className="mt-2 bg-avx-bg border-avx-line h-11 font-mono" value={form.amount_paid} onChange={(e) => setForm({ ...form, amount_paid: e.target.value })} data-testid="assign-amount" />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="label-mono">Payment method</Label>
                            <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                                <SelectTrigger className="mt-2 bg-avx-bg border-avx-line h-11"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="manual">Manual</SelectItem>
                                    <SelectItem value="transfer">Bank transfer</SelectItem>
                                    <SelectItem value="card">Card</SelectItem>
                                    <SelectItem value="paypal">PayPal</SelectItem>
                                    <SelectItem value="other">Other</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="label-mono">Status</Label>
                            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                                <SelectTrigger className="mt-2 bg-avx-bg border-avx-line h-11" data-testid="assign-status"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="pending">Pending</SelectItem>
                                    <SelectItem value="paid">Paid</SelectItem>
                                    <SelectItem value="cancelled">Cancelada</SelectItem>
                                    <SelectItem value="refunded">Reembolsada</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div>
                        <Label className="label-mono">Receipt (optional URL)</Label>
                        <Input className="mt-2 bg-avx-bg border-avx-line h-11 font-mono" value={form.proof_file_url} onChange={(e) => setForm({ ...form, proof_file_url: e.target.value })} />
                    </div>
                    <div>
                        <Label className="label-mono">Internal notes</Label>
                        <Textarea rows={3} className="mt-2 bg-avx-bg border-avx-line" value={form.internal_notes} onChange={(e) => setForm({ ...form, internal_notes: e.target.value })} />
                    </div>
                    <button type="submit" disabled={submitting} className="btn-primary w-full py-3 text-xs font-mono uppercase tracking-[0.2em] rounded-sm" data-testid="assign-submit-btn">
                        {submitting ? "Saving…" : "Register purchase"}
                    </button>
                </div>
            </form>
        </DashboardLayout>
    );
}
